import { Spinner, Alert, Button, Form, ActionGroup } from '@patternfly/react-core';
import React, { ReactElement, useEffect, useState } from 'react';
import './LoginPage.css';
import CenteredPage from '../../../layout/CenteredPage';
import { useLocation, useNavigate } from 'react-router-dom';
import { OtpInput } from './OtpInput';
import { LoginMFA as verifyMfa, ResendMFA } from '../../../api/login/Login';
import { getCSRFToken } from '../../../api/security/CSRFToken';
import axiosConfig, { IZIApiError } from '../../../api/axiosConfig';
import axios, { AxiosError } from 'axios';

// Maximum number of OTP entry attempts allowed before the user must request a new code.
const MAX_OTP_ATTEMPTS = 3;

export default function LoginMFA(): ReactElement {
	const [otp, setOtp] = useState('');
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState<React.ReactElement | null>();
	const [failedOtp, setFailedOtp] = useState(false);
	// tracks how many invalid OTP submissions have occurred. Only once this
	// reaches MAX_OTP_ATTEMPTS does the user get locked out and required to
	// request a new code (failedOtp) — before that, they can freely correct and
	// resubmit without needing to hit "Resend".
	const [otpAttempts, setOtpAttempts] = useState(0);
	const navigate = useNavigate();
	const location = useLocation();
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	const mfaToken = location.state?.mfa_token as string;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	const userEmail = location.state?.email as string;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	const userTokenExpiry = location.state?.token_expiry as number;

	useEffect(() => {
		if (!mfaToken) {
			navigate('/login');
			return;
		}

		getCSRFToken()
			.then((token) => {
				axiosConfig.instance.defaults.headers['CSRF-TOKEN'] = token;
			})
			.catch((err) => {
				console.error(err);
			});
	}, [mfaToken, navigate]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
		if (event.key === 'Enter') {
			verifyOtp();
		}
	};

	const verifyOtp = () => {
		if (loading) {
			return;
		}

		if (otp === '') {
			setMessage(
				<Alert
					variant="danger"
					isInline
					isPlain
					title="Verification Code must be set"
				/>
			);
			return;
		}

		if (otp.length != 6) {
			setMessage(
				<Alert
					variant="danger"
					isInline
					isPlain
					title="Verification Code must be exactly 6 digits"
				/>
			);
			return;
		}

		setMessage(null);
		setLoading(true);

		verifyMfa(mfaToken, otp)
			.then(() => {
				navigate('/getting-started');
			})
			.catch((err: unknown) => {
				if (axios.isAxiosError(err)) {
					const axiosErr: AxiosError = err as AxiosError;
					console.error('login MFA error', axiosErr);

					// count this failed attempt. Only lock the user out (hide
					// input, disable submit, force a new OTP request) once
					// they've exhausted MAX_OTP_ATTEMPTS — otherwise let them
					// correct and resubmit immediately.
					const attemptsSoFar = otpAttempts + 1;
					setOtpAttempts(attemptsSoFar);

					const attemptsRemaining = MAX_OTP_ATTEMPTS - attemptsSoFar;
					const hasAttemptsRemaining = attemptsRemaining > 0;

					setFailedOtp(!hasAttemptsRemaining);

					// Clear the entered code so the user isn't stuck resubmitting
					// the same invalid value.
					setOtp('');

					const attemptSuffix = attemptsRemaining === 1 ? '' : 's';
					const errorTitle = hasAttemptsRemaining
						? `The entered OTP is incorrect. You have ${attemptsRemaining} attempt${attemptSuffix} remaining.`
						: 'The entered OTP is incorrect. Please go back and generate a new OTP Code';

					setMessage(
						<Alert
							variant="danger"
							isInline
							isPlain
							title={errorTitle}
							data-testid="mfa-error"
						/>
					);
				}

				setLoading(false);
			});
	};

	const resendOtp = () => {
		ResendMFA(mfaToken)
			.then(() => {
				// requesting a new code resets the attempt count and unlocks
				// the input, since the user now has a fresh OTP to try.
				setOtpAttempts(0);
				setFailedOtp(false);
				setOtp('');

				setMessage(
					<Alert
						variant="success"
						isInline
						isPlain
						title="OTP request has succesfully been re-sent"
					/>
				);
			})
			.catch((err: unknown) => {
				if (axios.isAxiosError(err)) {
					const axiosErr: AxiosError = err as AxiosError;

					setMessage(
						<Alert
							variant="danger"
							isInline
							isPlain
							title={
								axiosErr.response?.data
									? (axiosErr.response?.data as IZIApiError).message
									: 'Something went wrong, please contact the administrator'
							}
						/>
					);
				}
			});
	};

	const verifyButton = loading ? (
		<div className="spinner-container">
			<Spinner size="lg" />
		</div>
	) : (
		<Button
			onClick={verifyOtp}
			variant="primary"
			data-testid="mfa-submit"
			type="button"
			className="btn-login"
			isDisabled={failedOtp}
		>
			Verify Code
		</Button>
	);

	return (
		<CenteredPage
			description={`We have sent a 6-digit verification code to your email address ${
				userEmail ? `(${userEmail})` : ''
			}. Please enter it below to continue.`}
			notice={message}
		>
			<Form
				autoComplete="one-time-code"
				onKeyDown={handleKeyDown}
			>
				{!failedOtp && (
					<OtpInput
						// Force remount on each failed attempt so OtpInput's internal
						// input state fully resets in sync with otp being cleared above.
						key={otpAttempts}
						onChange={(otp) => setOtp(otp)}
						userTokenExpiry={userTokenExpiry}
					/>
				)}
				<ActionGroup>{verifyButton}</ActionGroup>
				<br />
				<div style={{ textAlign: 'center' }}>
					<Button
						variant="link"
						isInline
						onClick={() => {
							navigate('/login');
						}}
						data-testid="mfa-back"
					>
						Go back
					</Button>
					<br />
					<Button
						variant="link"
						isInline
						onClick={resendOtp}
						data-testid="mfa-resend"
					>
						Didn't receive the code? Resend
					</Button>
				</div>
			</Form>
		</CenteredPage>
	);
}
